import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '@/shared/lib/api';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  Send,
  X,
  AlertCircle,
  MessageCircle,
  GraduationCap,
  TrendingUp,
  Target,
  HelpCircle,
  Play,
  Calendar,
  Trophy,
  BarChart3,
  ArrowLeft,
  Users,
  User
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge
} from '@travellegacy/nexops-design-system';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Message {
  id: string;
  userMessage: string;
  aiClientMessage: string;
  score: number;
  feedbackEmpatia: number | null;
  feedbackClareza: number | null;
  feedbackTom: number | null;
  feedbackResolucao: number | null;
  feedbackText: string;
  messageOrder: number;
}

interface Session {
  id: string;
  scenario: string;
  startedAt: string;
  finishedAt: string | null;
  finalScore: number | null;
  totalMessages: number;
}

interface SessionListItem {
  id: string;
  scenario: string;
  startedAt: string;
  finishedAt: string | null;
  finalScore: number | null;
  totalMessages: number;
  avgEmpatia: number | null;
  avgClareza: number | null;
  avgTom: number | null;
  avgResolucao: number | null;
}

interface AdminStatsResponse {
  totalSessions: number;
  totalUsers: number;
  averageScore: number;
  userStats: Array<{
    userId: string;
    userName: string;
    sessionsCount: number;
    averageScore: number;
    lastSessionAt: string;
  }>;
}

export function CommunicationTrainingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [viewAsUser, setViewAsUser] = useState(false); // Para admin poder ver como usuário normal
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null); // ID do usuário selecionado para ver sessões
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Buscar estatísticas de admin
  const { data: adminStats } = useQuery<AdminStatsResponse>({
    queryKey: ['training-admin-stats'],
    queryFn: async () => {
      const response = await api.get('/training/admin/stats');
      return response.data;
    },
    enabled: isAdmin && !viewAsUser && !selectedUserId,
  });

  // Buscar sessões do usuário selecionado (quando admin)
  const { data: userSessionsData, refetch: refetchUserSessions } = useQuery<{ sessions: SessionListItem[] }>({
    queryKey: ['training-user-sessions', selectedUserId],
    queryFn: async () => {
      const response = await api.get(`/training/admin/users/${selectedUserId}/sessions`);
      return response.data;
    },
    enabled: isAdmin && !!selectedUserId && !session,
  });

  // Buscar sessões do usuário logado
  const { data: sessionsData, refetch: refetchSessions } = useQuery<{ sessions: SessionListItem[] }>({
    queryKey: ['training-sessions'],
    queryFn: async () => {
      const response = await api.get('/training/sessions');
      return response.data;
    },
    enabled: (!isAdmin || viewAsUser) && !session && !selectedUserId,
  });

  const sessions = selectedUserId ? (userSessionsData?.sessions || []) : (sessionsData?.sessions || []);

  // Resetar estado quando navegar para a página
  useEffect(() => {
    // Quando o pathname é exatamente /training/communication, resetar para mostrar histórico
    if (location.pathname === '/training/communication') {
      setSession(null);
      setMessages([]);
      setCurrentMessage('');
      setIsFinished(false);
      setError(null);
      setSelectedUserId(null);
      setViewAsUser(false);
    }
  }, [location.pathname]);

  // Scroll para última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Iniciar sessão
  const startSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.post('/training/sessions/start', {});
      setSession(response.data.session);

      // Adicionar mensagem inicial do "cliente"
      const initialMsg = {
        id: 'initial',
        userMessage: '',
        aiClientMessage: response.data.initialMessage,
        score: 0,
        feedbackEmpatia: null,
        feedbackClareza: null,
        feedbackTom: null,
        feedbackResolucao: null,
        feedbackText: '',
        messageOrder: 0,
      };
      setMessages([initialMsg]);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao iniciar sessão');
    } finally {
      setIsLoading(false);
    }
  };

  // Enviar mensagem
  const sendMessage = async () => {
    if (!currentMessage.trim() || !session || isLoading) return;

    const messageText = currentMessage.trim();

    // Optimistic UI: adicionar mensagem do usuário imediatamente
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      userMessage: messageText,
      aiClientMessage: '',
      score: 0,
      feedbackEmpatia: null,
      feedbackClareza: null,
      feedbackTom: null,
      feedbackResolucao: null,
      feedbackText: '',
      messageOrder: messages.length,
    };

    setMessages((prev) => [...prev, tempUserMessage]);
    setCurrentMessage('');

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post(`/training/sessions/${session.id}/message`, {
        message: messageText,
      });

      // Substituir mensagem temporária pela resposta real do servidor
      setMessages((prev) => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id);
        return [...withoutTemp, response.data.message];
      });

      if (response.data.sessionFinished) {
        setIsFinished(true);
      }
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      // Remover mensagem temporária em caso de erro
      setMessages((prev) => prev.filter(m => m.id !== tempUserMessage.id));
      setError(error.response?.data?.error || 'Erro ao enviar mensagem');
    } finally {
      setIsLoading(false);
    }
  };

  // Encerrar sessão manualmente
  const finishSession = async () => {
    if (!session || isLoading) return;

    try {
      setIsLoading(true);
      await api.post(`/training/sessions/${session.id}/finish`, {});
      setIsFinished(true);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao encerrar sessão');
    } finally {
      setIsLoading(false);
    }
  };

  // Carregar sessão existente
  const loadSession = async (sessionId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/training/sessions/${sessionId}`);
      const { session: sessionData, messages: messagesData } = response.data;

      setSession(sessionData);
      setMessages(messagesData);
      setIsFinished(sessionData.finishedAt !== null);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao carregar sessão');
    } finally {
      setIsLoading(false);
    }
  };

  // Reiniciar
  const restart = () => {
    setSession(null);
    setMessages([]);
    setCurrentMessage('');
    setIsFinished(false);
    setError(null);
    setSelectedUserId(null);
    if (selectedUserId) {
      refetchUserSessions();
    } else {
      refetchSessions();
    }
  };

  // Formatar data de forma segura
  const formatSafeDate = (date: string | Date) => {
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return 'Data inválida';
      }
      return format(parsedDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  // Calcular melhor score de forma segura
  const getBestScore = (sessions: SessionListItem[]) => {
    if (sessions.length === 0) return 0;
    const scores = sessions.map(s => s.finalScore || 0).filter(score => score > 0);
    return scores.length > 0 ? Math.max(...scores) : 0;
  };

  // Renderizar nota com cor
  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600 dark:text-green-400';
    if (score >= 6) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 8) return 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800';
    if (score >= 6) return 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800';
    return 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
  };

  const getScoreBadgeBg = (score: number) => {
    if (score >= 8) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700';
    if (score >= 6) return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700';
    return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700';
  };

  const getScoreBadgeVariant = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 8) return 'success';
    if (score >= 6) return 'warning';
    return 'error';
  };

  // Modal "Como funciona"
  const HelpModal = () => {
    if (!showHelpModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHelpModal(false)}>
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Como funciona?</CardTitle>
                  <CardDescription>Aprenda praticando em situações reais</CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowHelpModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  1
                </div>
                <div>
                  <p className="font-medium text-foreground">IA gera cenário único e inteligente</p>
                  <p className="text-muted-foreground">Sistema adapta dificuldade baseado no seu desempenho anterior</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  2
                </div>
                <div>
                  <p className="font-medium text-foreground">Cliente simulado com contexto Travel Legacy</p>
                  <p className="text-muted-foreground">Situações reais: milhas, passagens, emergências, reclamações</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  3
                </div>
                <div>
                  <p className="font-medium text-foreground">Você responde como atendente</p>
                  <p className="text-muted-foreground">Demonstre empatia, clareza e profissionalismo</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  4
                </div>
                <div>
                  <p className="font-medium text-foreground">IA avalia e continua conversa</p>
                  <p className="text-muted-foreground">Feedback em tempo real com nota de 0 a 10 por critério</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  5
                </div>
                <div>
                  <p className="font-medium text-foreground">Resumo final com pontos de melhoria</p>
                  <p className="text-muted-foreground">Análise detalhada: empatia, clareza, tom e resolução</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Critérios de Avaliação</h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Empatia</p>
                  <p className="text-muted-foreground">Reconheceu sentimentos do cliente?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Clareza</p>
                  <p className="text-muted-foreground">Comunicação clara e objetiva?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Tom</p>
                  <p className="text-muted-foreground">Profissional mas cordial?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Resolução</p>
                  <p className="text-muted-foreground">Propôs soluções concretas?</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Tela inicial - Lista de sessões
  if (!session) {
    // Admin View - Visão geral de todos os usuários
    if (isAdmin && !viewAsUser && !selectedUserId) {
      return (
        <div className="space-y-6">
          {/* Header Admin */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Users className="h-8 w-8" />
                Treinamentos - Visão Admin
              </h1>
              <p className="text-muted-foreground mt-2">
                Acompanhe o desempenho de todos os usuários em treinamentos de comunicação
              </p>
            </div>
            <Button variant="outline" onClick={() => setViewAsUser(true)}>
              <User className="h-4 w-4 mr-2" />
              Ver como usuário
            </Button>
          </div>

          {/* Estatísticas gerais */}
          {adminStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total de Sessões</p>
                      <p className="text-2xl font-bold">{adminStats.totalSessions}</p>
                    </div>
                    <Trophy className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total de Usuários</p>
                      <p className="text-2xl font-bold">{adminStats.totalUsers}</p>
                    </div>
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Média Geral</p>
                      <p className={`text-2xl font-bold ${getScoreColor(adminStats.averageScore)}`}>
                        {adminStats.averageScore.toFixed(1)}
                      </p>
                    </div>
                    <BarChart3 className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabela de usuários */}
          <Card>
            <CardHeader>
              <CardTitle>Desempenho por Usuário</CardTitle>
              <CardDescription>
                {adminStats ? `${adminStats.userStats.length} usuário(s) com treinamentos` : 'Carregando...'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!adminStats || adminStats.userStats.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Nenhum usuário realizou treinamentos ainda.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Usuário</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Sessões</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Média</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Último Treino</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminStats.userStats.map((userStat) => (
                        <tr
                          key={userStat.userId}
                          className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedUserId(userStat.userId)}
                        >
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{userStat.userName}</span>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <Badge>{userStat.sessionsCount}</Badge>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`text-lg font-bold ${getScoreColor(userStat.averageScore)}`}>
                              {userStat.averageScore.toFixed(1)}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              {formatSafeDate(userStat.lastSessionAt)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <HelpModal />
        </div>
      );
    }

    // Visão de sessões de usuário específico (quando admin seleciona um usuário)
    if (isAdmin && selectedUserId) {
      const selectedUser = adminStats?.userStats.find(u => u.userId === selectedUserId);
      const completedSessions = sessions.filter(s => s.finishedAt && s.finalScore !== null);
      const avgScore = completedSessions.length > 0
        ? completedSessions.reduce((sum, s) => sum + (s.finalScore || 0), 0) / completedSessions.length
        : 0;

      return (
        <div className="space-y-6">
          {/* Header com botão voltar */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedUserId(null)} className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para visão geral
              </Button>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <User className="h-8 w-8" />
                Treinamentos - {selectedUser?.userName || 'Usuário'}
              </h1>
              <p className="text-muted-foreground mt-2">
                Visualizando sessões de treinamento deste usuário
              </p>
            </div>
          </div>

          {/* Estatísticas do usuário */}
          {completedSessions.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Sessões Completas</p>
                      <p className="text-2xl font-bold">{completedSessions.length}</p>
                    </div>
                    <Trophy className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Média Geral</p>
                      <p className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>
                        {avgScore.toFixed(1)}
                      </p>
                    </div>
                    <BarChart3 className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Mensagens</p>
                      <p className="text-2xl font-bold">
                        {completedSessions.reduce((sum, s) => sum + s.totalMessages, 0)}
                      </p>
                    </div>
                    <MessageCircle className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Melhor Score</p>
                      <p className={`text-2xl font-bold ${getScoreColor(getBestScore(completedSessions))}`}>
                        {getBestScore(completedSessions).toFixed(1)}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabela de histórico do usuário */}
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Treinamentos</CardTitle>
              <CardDescription>
                {sessions.length === 0 ? 'Nenhum treinamento realizado ainda' : `${sessions.length} sessão(ões) registrada(s)`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Este usuário ainda não realizou nenhum treinamento.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Data</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Cenário</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Mensagens</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Score Final</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Empatia</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Clareza</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Tom</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Resolução</th>
                        <th className="text-center p-3 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((session) => (
                        <tr
                          key={session.id}
                          className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => loadSession(session.id)}
                        >
                          <td className="p-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              {formatSafeDate(session.startedAt)}
                            </div>
                          </td>
                          <td className="p-3">
                            <p className="text-sm font-medium">{session.scenario}</p>
                          </td>
                          <td className="p-3 text-center">
                            <Badge>{session.totalMessages}</Badge>
                          </td>
                          <td className="p-3 text-center">
                            {session.finalScore !== null && session.finalScore !== undefined ? (
                              <span className={`text-lg font-bold ${getScoreColor(session.finalScore)}`}>
                                {session.finalScore.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {session.avgEmpatia !== null && session.avgEmpatia !== undefined ? (
                              <span className={`text-sm font-medium ${getScoreColor(session.avgEmpatia)}`}>
                                {session.avgEmpatia.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {session.avgClareza !== null && session.avgClareza !== undefined ? (
                              <span className={`text-sm font-medium ${getScoreColor(session.avgClareza)}`}>
                                {session.avgClareza.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {session.avgTom !== null && session.avgTom !== undefined ? (
                              <span className={`text-sm font-medium ${getScoreColor(session.avgTom)}`}>
                                {session.avgTom.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {session.avgResolucao !== null && session.avgResolucao !== undefined ? (
                              <span className={`text-sm font-medium ${getScoreColor(session.avgResolucao)}`}>
                                {session.avgResolucao.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {session.finishedAt ? (
                              <Badge variant={session.finalScore && session.finalScore >= 7 ? 'success' : 'warning'}>
                                Concluída
                              </Badge>
                            ) : (
                              <Badge>Em andamento</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <HelpModal />
        </div>
      );
    }

    // Visão normal de usuário (ou admin em modo usuário)
    const completedSessions = sessions.filter(s => s.finishedAt && s.finalScore !== null);
    const avgScore = completedSessions.length > 0
      ? completedSessions.reduce((sum, s) => sum + (s.finalScore || 0), 0) / completedSessions.length
      : 0;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            {isAdmin && viewAsUser && (
              <Button variant="ghost" size="sm" onClick={() => setViewAsUser(false)} className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para visão admin
              </Button>
            )}
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <GraduationCap className="h-8 w-8" />
              Treinamento de Comunicação
            </h1>
            <p className="text-muted-foreground mt-2">
              Pratique suas habilidades de atendimento ao cliente em cenários realistas com IA
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => setShowHelpModal(true)}>
              <HelpCircle className="h-4 w-4 mr-2" />
              Como funciona
            </Button>
            <Button onClick={startSession} disabled={isLoading}>
              <Play className="h-4 w-4 mr-2" />
              {isLoading ? 'Iniciando...' : 'Nova Simulação'}
            </Button>
          </div>
        </div>

        {/* Estatísticas gerais */}
        {completedSessions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sessões Completas</p>
                    <p className="text-2xl font-bold">{completedSessions.length}</p>
                  </div>
                  <Trophy className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Média Geral</p>
                    <p className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>
                      {avgScore.toFixed(1)}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Mensagens</p>
                    <p className="text-2xl font-bold">
                      {completedSessions.reduce((sum, s) => sum + s.totalMessages, 0)}
                    </p>
                  </div>
                  <MessageCircle className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Melhor Score</p>
                    <p className={`text-2xl font-bold ${getScoreColor(getBestScore(completedSessions))}`}>
                      {getBestScore(completedSessions).toFixed(1)}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabela de histórico */}
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Treinamentos</CardTitle>
            <CardDescription>
              {sessions.length === 0 ? 'Nenhum treinamento realizado ainda' : `${sessions.length} sessão(ões) registrada(s)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-12">
                <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Comece sua jornada de aprendizado! Inicie sua primeira simulação para praticar suas habilidades.
                </p>
                <Button onClick={startSession} disabled={isLoading}>
                  <Play className="h-4 w-4 mr-2" />
                  Iniciar Primeira Simulação
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Data</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Cenário</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Mensagens</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Score Final</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Empatia</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Clareza</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Tom</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Resolução</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <tr
                        key={session.id}
                        className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => loadSession(session.id)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {formatSafeDate(session.startedAt)}
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-sm font-medium">{session.scenario}</p>
                        </td>
                        <td className="p-3 text-center">
                          <Badge>{session.totalMessages}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          {session.finalScore !== null && session.finalScore !== undefined ? (
                            <span className={`text-lg font-bold ${getScoreColor(session.finalScore)}`}>
                              {session.finalScore.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {session.avgEmpatia !== null && session.avgEmpatia !== undefined ? (
                            <span className={`text-sm font-medium ${getScoreColor(session.avgEmpatia)}`}>
                              {session.avgEmpatia.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {session.avgClareza !== null && session.avgClareza !== undefined ? (
                            <span className={`text-sm font-medium ${getScoreColor(session.avgClareza)}`}>
                              {session.avgClareza.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {session.avgTom !== null && session.avgTom !== undefined ? (
                            <span className={`text-sm font-medium ${getScoreColor(session.avgTom)}`}>
                              {session.avgTom.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {session.avgResolucao !== null && session.avgResolucao !== undefined ? (
                            <span className={`text-sm font-medium ${getScoreColor(session.avgResolucao)}`}>
                              {session.avgResolucao.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {session.finishedAt ? (
                            <Badge variant={session.finalScore && session.finalScore >= 7 ? 'success' : 'warning'}>
                              Concluída
                            </Badge>
                          ) : (
                            <Badge>Em andamento</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        <HelpModal />
      </div>
    );
  }

  // Resumo final
  if (isFinished && messages.length > 1) {
    const validMessages = messages.filter((m) => m.messageOrder > 0);
    const avgScore =
      validMessages.reduce((sum, m) => sum + m.score, 0) / validMessages.length;
    const avgEmpatia =
      validMessages.reduce((sum, m) => sum + (m.feedbackEmpatia || 0), 0) /
      validMessages.length;
    const avgClareza =
      validMessages.reduce((sum, m) => sum + (m.feedbackClareza || 0), 0) /
      validMessages.length;
    const avgTom =
      validMessages.reduce((sum, m) => sum + (m.feedbackTom || 0), 0) / validMessages.length;
    const avgResolucao =
      validMessages.reduce((sum, m) => sum + (m.feedbackResolucao || 0), 0) /
      validMessages.length;

    return (
      <div className="space-y-6">
        {/* Botão voltar */}
        <Button variant="ghost" size="sm" onClick={restart} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Histórico
        </Button>

        {/* Header do resumo */}
        <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/20">
          <CardContent className="pt-8 pb-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Treinamento Concluído!</h2>
              <p className="text-muted-foreground mb-6">
                Você completou a simulação. Veja seu desempenho abaixo.
              </p>

              <div className="flex items-center justify-center gap-3">
                <span className="text-sm text-muted-foreground">Pontuação Final:</span>
                <div className={`px-6 py-3 rounded-full text-4xl font-bold ${getScoreBadgeBg(avgScore)}`}>
                  {avgScore.toFixed(1)}
                  <span className="text-lg ml-1">/10</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Métricas detalhadas */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhamento por Critério</CardTitle>
            <CardDescription>Análise das 4 dimensões de comunicação</CardDescription>
          </CardHeader>
          <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Empatia</span>
                    <span className={`text-xl font-bold ${getScoreColor(avgEmpatia)}`}>
                      {avgEmpatia.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        avgEmpatia >= 8
                          ? 'bg-green-500'
                          : avgEmpatia >= 6
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${(avgEmpatia / 10) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Clareza</span>
                    <span className={`text-xl font-bold ${getScoreColor(avgClareza)}`}>
                      {avgClareza.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        avgClareza >= 8
                          ? 'bg-green-500'
                          : avgClareza >= 6
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${(avgClareza / 10) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Tom</span>
                    <span className={`text-xl font-bold ${getScoreColor(avgTom)}`}>
                      {avgTom.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        avgTom >= 8 ? 'bg-green-500' : avgTom >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(avgTom / 10) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Resolução</span>
                    <span className={`text-xl font-bold ${getScoreColor(avgResolucao)}`}>
                      {avgResolucao.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        avgResolucao >= 8
                          ? 'bg-green-500'
                          : avgResolucao >= 6
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${(avgResolucao / 10) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        {/* Histórico da conversa */}
        <Card>
            <CardHeader>
              <CardTitle>Histórico da Conversa</CardTitle>
              <CardDescription>Revise suas respostas e feedbacks recebidos</CardDescription>
            </CardHeader>
            <CardContent className="max-h-96 overflow-y-auto">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div key={msg.id}>
                    {/* Mensagem do cliente */}
                    {msg.aiClientMessage && (
                      <div className="flex justify-start mb-3">
                        <Card className="max-w-[80%] shadow-sm">
                          <CardContent className="pt-4">
                            <p className="text-sm leading-relaxed">{msg.aiClientMessage}</p>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Mensagem do usuário */}
                    {msg.userMessage && (
                      <div className="flex justify-end mb-3">
                        <Card className="max-w-[80%] bg-primary text-primary-foreground shadow-sm">
                          <CardContent className="pt-4">
                            <p className="text-sm leading-relaxed">{msg.userMessage}</p>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Feedback */}
                    {msg.messageOrder > 0 && msg.feedbackText && (
                      <div className="mb-6">
                        <Card className={`${getScoreBgColor(msg.score)} shadow-md border-2`}>
                          <CardContent className="pt-5 pb-5">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center">
                                  <Target className="w-4 h-4 text-white" />
                                </div>
                                <span className="text-xs font-medium text-muted-foreground">
                                  Resposta {msg.messageOrder}
                                </span>
                              </div>
                              <Badge variant={getScoreBadgeVariant(msg.score)}>
                                {msg.score.toFixed(1)} / 10
                              </Badge>
                            </div>
                            <p className="text-sm text-foreground leading-relaxed">
                              {msg.feedbackText}
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

        {/* Ações finais */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={restart}>
            Ver Histórico
          </Button>
          <Button onClick={startSession} disabled={isLoading}>
            <Play className="h-4 w-4 mr-2" />
            Nova Simulação
          </Button>
        </div>
      </div>
    );
  }

  // Chat ativo ou visualização
  const isViewOnly = selectedUserId !== null; // Se está visualizando sessão de outro usuário (admin)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={restart}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">
              {isViewOnly ? 'Visualizando Sessão' : 'Simulação em Andamento'}
            </h2>
            <p className="text-sm text-muted-foreground">Cenário: {session.scenario}</p>
          </div>
        </div>
        {!isViewOnly && !isFinished && (
          <Button variant="outline" onClick={finishSession} disabled={isLoading}>
            <X className="h-4 w-4 mr-2" />
            Encerrar
          </Button>
        )}
      </div>

      {/* Chat */}
      <Card>
        <CardContent className="p-6">
          <div className={`${isViewOnly ? 'h-[600px]' : 'h-[500px]'} overflow-y-auto ${isViewOnly ? '' : 'mb-4'} space-y-4`}>
            {messages.map((msg) => (
              <div key={msg.id}>
                {/* Mensagem do cliente (mostrar apenas se for messageOrder 0 ou se for a próxima pergunta) */}
                {msg.messageOrder === 0 && msg.aiClientMessage && (
                  <div className="flex justify-start mb-3">
                    <Card className="max-w-[70%] shadow-sm">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Cliente</span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.aiClientMessage}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Mensagem do usuário */}
                {msg.userMessage && (
                  <div className="flex justify-end mb-3">
                    <Card className="max-w-[70%] bg-primary text-primary-foreground shadow-sm">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium opacity-90">Você</span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.userMessage}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Card de feedback */}
                {msg.messageOrder > 0 && msg.feedbackText && (
                  <Card className={`${getScoreBgColor(msg.score)} shadow-md border-2 mb-6`}>
                    <CardContent className="pt-5 pb-5">
                      {/* Header com ícone e score */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">Avaliação da IA</p>
                            <p className="text-xs text-muted-foreground">Análise da sua resposta</p>
                          </div>
                        </div>
                        <div className={`px-4 py-2 rounded-full ${getScoreBadgeBg(msg.score)}`}>
                          <span className="text-2xl font-bold">{msg.score.toFixed(1)}</span>
                          <span className="text-sm font-medium ml-1">/10</span>
                        </div>
                      </div>

                      {/* Feedback text */}
                      <p className="text-sm text-foreground leading-relaxed mb-4">
                        {msg.feedbackText}
                      </p>

                      {/* Métricas individuais */}
                      <div className="grid grid-cols-4 gap-2 pt-3 border-t">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Empatia</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackEmpatia || 0)}`}>
                            {msg.feedbackEmpatia?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Clareza</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackClareza || 0)}`}>
                            {msg.feedbackClareza?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Tom</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackTom || 0)}`}>
                            {msg.feedbackTom?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Resolução</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackResolucao || 0)}`}>
                            {msg.feedbackResolucao?.toFixed(1) || '-'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Próxima mensagem do cliente (aparece depois do feedback) */}
                {msg.messageOrder > 0 && msg.aiClientMessage && (
                  <div className="flex justify-start mb-3">
                    <Card className="max-w-[70%] shadow-sm">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Cliente</span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.aiClientMessage}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator - mostra quando está carregando */}
            {isLoading && (
              <div className="flex justify-start mb-3">
                <Card className="max-w-[70%] shadow-sm">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="text-xs text-muted-foreground">IA está pensando...</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input - apenas para usuário ativo, não para visualização admin */}
          {!isViewOnly && (
            <>
              <div className="flex gap-2 mt-4">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Digite sua resposta..."
                  className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  disabled={isLoading}
                />
                <Button onClick={sendMessage} disabled={isLoading || !currentMessage.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>

              {error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                </div>
              )}
            </>
          )}

          {/* Indicador de visualização apenas leitura */}
          {isViewOnly && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Você está visualizando uma sessão de outro usuário (modo somente leitura)
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
